import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { saveAs } from "file-saver";
import { useStoreState } from "ezStore";

import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { Button, Checkbox, FormGroup, Input, Select } from "@cofano/cck";
import { ColumnSettingsProps, FilterList } from "@cofano/cck/Table/Table";

import { Tooltip } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";

import { request } from "components/utils/Request2";
import { convertFilterToJSON } from "components/cargo/CargoFilter";
import { useConfirm } from "components/hooks/useConfirm";
import { FormDialog, FormDialogComponentProps, useFormDialog } from "components/dialogs/Dialogs";
import FormField from "components/cck/FormField";
import RelationSelect from "view/relations/components/selects/RelationSelect";

import { Language } from "imports/Utils";

import { Cargo, Relation } from "types/index";

const CargosDownload = ({ selection, isFullSelect, filters, columns, totalResults, onDownload }: {
	selection: Cargo[];
	isFullSelect: boolean;
	filters: FilterList;
	columns: ColumnSettingsProps[];
	totalResults: number;
	onDownload?: () => void;
}) => {

	const { formDialogProps, openFormDialog } = useFormDialog<Cargo[]>({
		onSave: () => {
			if (onDownload) {
				onDownload();
			}
		},
	});

	const columnIds = columns.filter((item) => item.visible && !item.column.disabled).sort((a, b) => a.order - b.order).map((item) => item.column.id);
	const disabled = (selection.length === 0) || (selection.length > 200) || (isFullSelect && totalResults > 200);

	return (
		<React.Fragment>
			<Tooltip title={disabled ? intl.formatMessage({ id: "PLEASE_SELECT_LESS_THAN_200_CONTAINERS" }) : undefined}>
				<span>
					<Button
						disabled={disabled}
						size="small"
						onClick={() => openFormDialog(selection)}
						variant="outlined">
						<GetAppIcon />
					</Button>
				</span>
			</Tooltip>

			<CargoDownloadDialog
				{...formDialogProps}
				filters={filters}
				columnIds={columnIds}
				isFullSelect={isFullSelect} />
		</React.Fragment>
	);
};

enum CargoDownloadType {
	Download = "DOWNLOAD",
	User = "USER",
	Relations = "RELATIONS",
	Email = "EMAIL"
}

type CargoDownloadForm = {
	type: CargoDownloadType,
	includeFilters: boolean,
	relations?: Relation[],
	emails: string,
}

const CargoDownloadDialog = ({
	entity,
	open,
	onClose,
	onSave,
	filters,
	columnIds,
	isFullSelect,
}: FormDialogComponentProps<Cargo[]> & {
	filters: FilterList;
	columnIds: string[];
	isFullSelect: boolean;
}) => {

	const user = useStoreState(state => state.session.user);
	const confirm = useConfirm();
	const { instance } = useCoastguard();

	const [ loading, setLoading ] = useState(false);

	const defaultValues = {
		type: CargoDownloadType.Download,
		includeFilters: true,
		relations: undefined,
		emails: "",
	};

	const { control, handleSubmit, reset, formState, watch } = useForm<CargoDownloadForm>({
		defaultValues: defaultValues,
	});

	const values = watch();

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const createParameter = (data: CargoDownloadForm) => {
		let param = "";

		if (data.type === CargoDownloadType.User) {
			param = `emailAddresses=${user?.email?.replace("+", "%2b")}`;
		} else if (data.type === CargoDownloadType.Relations) {
			const relationEmails = data.relations?.map(r => r.email) || [];

			relationEmails.forEach((email, index) => {
				param += `emailAddresses=${email?.replace("+", "%2b")}${index !== relationEmails.length - 1 ? "&" : ""}`;
			});
		} else if (data.type === CargoDownloadType.Email) {
			const splitEmails = data.emails.split(",");

			splitEmails.forEach((email, index) => {
				param += `emailAddresses=${email.trim().replace("+", "%2b")}${index !== splitEmails.length - 1 ? "&" : ""}`;
			});
		}

		param += `&all=${isFullSelect}&includeSearch=${data.includeFilters}`;

		return param;
	};

	const downloadCargos = (data: CargoDownloadForm) => {

		setLoading(true);

		const url = `/cargos/download?${createParameter(data)}`;

		instance.fetchBlob(url, {
			method: "POST",
			body: {
				cargoIds: entity?.map(s => s.id),
				columns: columnIds,
				search: convertFilterToJSON(filters),
				searchReadable: convertFiltersToReadable(filters),
			},
			headers: {
				Accept: "application/vnd.ms-excel",
			},
		}).then(async (blob) => {
			saveAs(blob, "cargos.xlsx");
		}).finally(() => {
			setLoading(false);
			handleOnClose();
			onSave();
		});
	};

	const sendContainers = (data: CargoDownloadForm) => {

		setLoading(true);

		const url = `cargos/send?${createParameter(data)}`;

		request({
			url: url,
			method: "POST",
			body: {
				cargoIds: entity?.map(s => s.id),
				columns: columnIds,
				search: convertFilterToJSON(filters),
			},
		}).then(() => {
			setLoading(false);
			handleOnClose();
			onSave();
		});
	};

	const save = (data: CargoDownloadForm) => {

		if (data.type === CargoDownloadType.Download) {
			downloadCargos(data);
		} else {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "CARGO_DOWNLOAD_CONFIRM_SEND" }),
				content: (
					<React.Fragment>
						{data.type === CargoDownloadType.User && (
							<React.Fragment>
								{user?.email}
							</React.Fragment>
						)}

						{data.type === CargoDownloadType.Relations && (
							<ul style={{ margin: 0 }}>
								{data.relations?.map((relation: Relation, index: number) => (
									<li key={index}>{relation.email}</li>
								))}
							</ul>
						)}

						{data.type === CargoDownloadType.Email && (
							<React.Fragment>
								{data.emails}
							</React.Fragment>
						)}
					</React.Fragment>
				),
			}).then(() => {
				sendContainers(data);
			});
		}
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "DOWNLOAD" })}
			form='cargoDownloadForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="cargoDownloadForm">
				<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="type"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								fetchOptions={Object.values(CargoDownloadType)}
								getOptionLabel={option => intl.formatMessage({ id: option })}
								clearable={false}
								disableSearch />
						)} />
				</FormGroup>

				<FormGroup label={Language("INCLUDE_FILTERS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="includeFilters"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								//disabled={filters.length === 0}
								disabled={false} />
						)} />
				</FormGroup>

				{values.type === CargoDownloadType.User && (
					<FormGroup label={Language("USER")} labelPlacement="left-center" overflow="hidden">
						{user?.email}
					</FormGroup>
				)}

				{(values.type === CargoDownloadType.Relations || values.type === CargoDownloadType.Email) && (
					<h4 style={{ margin: "24px 0 0", fontWeight: 600, fontStyle: "italic" }}>
						{Language("CARGO_DOWNLOAD_EMAIL_TO")}
					</h4>
				)}

				{values.type === CargoDownloadType.Relations && (
					<FormGroup label={Language("RELATION")}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="relations">
							<Controller
								name="relations"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<RelationSelect
										{...field}
										getOptionDisabled={option => !option.email}
										showEmail
										multiple />
								)} />
						</FormField>
					</FormGroup>
				)}

				{values.type === CargoDownloadType.Email && (
					<FormGroup label={Language("EMAIL")}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="emails">
							<Controller
								name="emails"
								control={control}
								rules={{ required: true }}
								render={({ field }) => <Input {...field} />} />
						</FormField>
					</FormGroup>
				)}
			</form>
		</FormDialog>
	);
};

function convertFiltersToReadable(filters: FilterList) {

	//@ts-expect-error
	const makeReadable = (value: unknown) => {
		if (value == null) {
			return "";
		} else if (Array.isArray(value)) {
			return value.map(makeReadable).join(", ");
		} else if (typeof value === "object") {
			//@ts-expect-error
			return value.name;
		} else {
			return value;
		}
	};

	const entries = filters.map(({ property, value }) => {
		return [ property, makeReadable(value) ];
	});

	return Object.fromEntries(entries);
}

export default CargosDownload;
